import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { ApiClientError } from '@/lib/api-client';
import { cn } from '@/lib/utils';

import { API_ERROR_DETAIL_PARAMS, apiErrorKey, isCatalogedApiErrorCode } from './catalog';

/**
 * Shared API-error localization (spec-13 §10).
 *
 * Maps a stable error code to localized recovery text, interpolating only
 * allowlisted `details` fields. `error.message` (raw backend diagnostic) is
 * available only inside the technical disclosure — audit-critical components
 * never render it directly.
 */
export interface LocalizedApiErrorText {
  /** Localized primary message. */
  message: string;
  /** Stable code (for callers that branch on it). */
  code: string;
  /** Correlation id when the server included one. */
  requestId?: string;
  /** Raw diagnostic for the technical disclosure. */
  debugMessage?: string;
  /** Whether the code was cataloged (false → generic fallback used). */
  cataloged: boolean;
}

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** Localize known list-valued detail items (direction codes, precheck keys). */
const localizeDetailItem = (t: TFn, key: string, item: unknown): string => {
  const raw = String(item);
  if (key === 'missing_directions' && ['left', 'right', 'rear', 'other'].includes(raw)) {
    return t(`efs.issues.directions.${raw}`);
  }
  if (key === 'blocking_reasons') {
    // Precheck keys are localized in the audit-package namespace.
    return t(`audit-package:efs.audit_package.checks.${raw}`, { defaultValue: raw });
  }
  return raw;
};

export function localizeApiError(t: TFn, error: unknown): LocalizedApiErrorText {
  if (!(error instanceof ApiClientError)) {
    return {
      message: t('efs.issues.errors.UNKNOWN_ERROR'),
      code: 'UNKNOWN_ERROR',
      debugMessage: error instanceof Error ? error.message : undefined,
      cataloged: false,
    };
  }

  const code = error.code;
  const cataloged = isCatalogedApiErrorCode(code);
  const params: Record<string, unknown> = {};
  if (cataloged) {
    const allowed = API_ERROR_DETAIL_PARAMS[code] ?? [];
    for (const key of allowed) {
      const value = error.details?.[key];
      if (value === null || value === undefined) continue;
      params[key] = Array.isArray(value)
        ? value.map((item) => localizeDetailItem(t, key, item)).join(' / ')
        : value;
    }
  }

  return {
    message: cataloged ? t(apiErrorKey(code), params) : t('efs.issues.errors.UNKNOWN_ERROR'),
    code,
    requestId: error.requestId,
    debugMessage: error.message,
    cataloged,
  };
}

/**
 * Inline localized error alert for mutations/queries. Renders beside the
 * affected action; keeps the raw diagnostic behind a collapsed disclosure
 * for authorized internal users.
 */
export const LocalizedApiError = ({
  error,
  showTechnicalDetails,
  className,
}: {
  error: unknown;
  /** Permission gate for the raw diagnostic (default false). */
  showTechnicalDetails?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation(['issues']);
  const [open, setOpen] = useState(false);
  if (!error) return null;

  const text = localizeApiError(t, error);

  return (
    <div
      role="alert"
      className={cn(
        'space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm',
        className,
      )}
    >
      <p className="text-efs-text-primary">{text.message}</p>
      {text.requestId ? (
        <p className="text-[11px] text-efs-text-muted">
          {t('efs.issues.states.request_id')}: <code>{text.requestId}</code>
        </p>
      ) : null}
      {showTechnicalDetails && text.debugMessage ? (
        <div>
          <button
            type="button"
            className="flex min-h-6 items-center gap-1 text-[11px] text-efs-text-muted hover:text-efs-text-primary"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? (
              <ChevronDown className="h-3 w-3" aria-hidden />
            ) : (
              <ChevronRight className="h-3 w-3" aria-hidden />
            )}
            {t('efs.issues.target.technical_details')}
          </button>
          {open ? (
            <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-all rounded bg-efs-bg-subtle p-1.5 text-[11px] text-efs-text-muted">
              {text.code}: {text.debugMessage}
            </pre>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
